/**
 * @file ConfirmWindow.js
 * @module src/js/modals/ConfirmWindow
 * @description
 * **Component to display a confirmation dialog.**
 * - Renders a modal overlay with a message and Confirm/Cancel buttons.
 * - Supports optional input field for user confirmation.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Disable Confirm button until correct input is provided when hasInput is true. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added enter and exit animations. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Clear timeout when a new dialog is opened. Use id to handle multiple consecutive dialogs. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../contexts/ThemeProvider";
import { useModal } from "../contexts/ModalProvider";

export default function ConfirmWindow() {
    const { theme } = useTheme();
    const { confirmDialog, setConfirmDialog } = useModal();
    const [inputValue, setInputValue] = useState("");
    const [isClosing, setIsClosing] = useState(false); // need this to let the element stay until animation ends
    const timeoutRef = useRef(null);

    const {
        id,
        hasInput, // if true, show textarea and resolve the Promise with the input value
        message,
        confirmLabel,
        cancelLabel,
        buttonColor,
        placeholder,
        resolve,
    } = confirmDialog || {};

    const enterDuration = 150; // fade in + pop
    const exitDuration = 100;  // fade out + shrink

    useEffect(() => {
        if (confirmDialog) {
            setIsClosing(false);
            setInputValue("");

            // useEffect runs after React commits the render to the DOM (after paint). It is not immediate.
            // The old timeout may still fire before the effect gets a chance to run and clear it.
            // So aside from clearing the timeout, we also check whether the confirmDialog is still the same using id
            // in the closeWithAnimation function.
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        }
    }, [confirmDialog]); // runs when confirmDialog either becomes the next dialog or null

    // Common close procedure (play animation → then unmount)
    const closeWithAnimation = useCallback((resolveValue) => {
        if (resolve) resolve(resolveValue);

        setIsClosing(true); // Play fade-out animation

        const closingId = id;
        // closingId: the dialog id captured when starting to close this dialog (e.g. dialog #1)
        // prev: whatever confirmDialog state is at the moment the timeout callback runs
        timeoutRef.current = setTimeout(() => {
            setConfirmDialog(prev => prev?.id === closingId ? null : prev);
        }, exitDuration); // Wait for the animation to finish before unmounting
    }, [resolve, setConfirmDialog, id]);

    const handleConfirmOk = useCallback(() => {
        closeWithAnimation(hasInput ? inputValue : true);
    }, [closeWithAnimation, hasInput, inputValue]);

    const handleConfirmCancel = useCallback(() => {
        closeWithAnimation(false);
    }, [closeWithAnimation]);
    
    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 99999,
                pointerEvents: "auto",
                fontSize: "0.9rem",
                backgroundColor:
                    theme === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(0, 0, 0, 0.3)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",

                // Fade-in and Fade-out animation
                animation: isClosing
                    ? `modalOverlayFadeOut ${exitDuration}ms ease-out forwards`
                    : `modalOverlayFadeIn ${enterDuration}ms ease-out forwards`,
            }}
        >
            <div
                style={{
                    backgroundColor:
                        theme === "dark" ? "#38424cff" : "#e9e9e9ff",
                    color: theme === "dark" ? "white" : "black",
                    border: `1.2px solid ${theme === "dark" ? "white" : "transparent"}`,
                    borderRadius: "10px",
                    padding: "16px 20px",
                    maxWidth: "420px",
                    width: "90%",
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",

                    // Pop-in and Pop-out animation
                    animation: isClosing
                        ? `modalDialogShrink ${exitDuration}ms ease-out forwards`
                        : `modalDialogPop ${enterDuration}ms ease-out forwards`,
                }}
            >
                {message && (
                    <p style={{ margin: hasInput ? "0 0 10px" : 0, whiteSpace: "pre-wrap" }}>
                        {message}
                    </p>
                )}

                {hasInput && (
                    <textarea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={placeholder}
                        style={{
                            width: "100%",
                            resize: "vertical",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            border: "1px solid rgba(0, 0, 0, 0.2)",
                            backgroundColor: "rgba(0, 0, 0, 0.3)",
                            color: theme === "dark" ? "white" : "black",
                            fontSize: "0.9rem",
                            boxSizing: "border-box",
                        }}
                    />
                )}

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "8px",
                        marginTop: "16px",
                    }}
                >
                    <button
                        type="button"
                        onClick={handleConfirmCancel}
                        style={{
                            padding: "6px 12px",
                            borderRadius: "4px",
                            outline: `2px solid ${theme === "dark" ? "white" : "black"}`,
                            outlineOffset: "-2px",
                            color: theme === "dark" ? "white" : "black",
                            cursor: "pointer",
                            alignItems: "center",
                            display: "flex",
                        }}
                    >
                        {cancelLabel || "Cancel"}
                    </button>

                    <button
                        type="button"
                        onClick={handleConfirmOk}
                        style={{
                            padding: "6px 12px",
                            borderRadius: "4px",
                            outline: `2px solid ${buttonColor}`,
                            outlineOffset: "-2px",
                            color: buttonColor,
                            cursor: "pointer",
                            alignItems: "center",
                            display: "flex",
                        }}
                        disabled={hasInput && inputValue !== placeholder}
                    >
                        {confirmLabel || "OK"}
                    </button>
                </div>
            </div>
        </div>
    );
}